import { useMemo, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2Button,
  V2EmptyState,
  V2DataGrid,
  V2FormGrid,
  V2FormField,
  V2FormActions,
  Icon,
  LoadingState,
  StatePanel,
  Combobox,
  type ComboboxOption,
  type V2TableColumn,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type {
  PurchaseOrder,
  PurchaseOrderListResponse,
  PurchaseOrderLineDraft,
  PurchaseOrderStatus,
  PurchaseRequestListResponse,
} from '../../lib/types/procurement.js';
import { orderStatusOf } from '../../lib/types/procurement.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { useAmountDisplay, useTenantSettings } from '../../lib/tenantSettings.js';
import { amountColumnHeader, formatRialFa } from '../../lib/finance-display.js';
import { directPurchaseOrderAllowed } from '../../lib/types/tenantSettings.js';

const STATUS_TONE: Record<PurchaseOrderStatus, V2BadgeTone> = {
  draft: 'neutral',
  submitted: 'info',
  approved: 'brand',
  in_transit: 'info',
  partially_received: 'warn',
  received: 'success',
  cancelled: 'neutral',
};

const EMPTY_LINE: PurchaseOrderLineDraft = {
  productRef: '',
  description: '',
  quantity: '',
  uomRef: '',
  unitPriceRial: '',
  notes: '',
};

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Procurement.dc.html §02 PURCHASE ORDERS
 * archetype: ENTITY_LIST + create form
 * status: KIT_CONFORMANT — supplier picker submits by real party **code**;
 *   the server is the actual gate on "is this party a supplier"
 *   (`resolveSupplierPartyId` in packages/modules/procurement/src/refs.ts
 *   requires a real `party_role.role_kind IN ('supplier','both')` row) —
 *   there is no list-suppliers-only endpoint, so the picker offers every
 *   real party and a non-supplier code fails honestly server-side rather
 *   than being silently pre-filtered on a guess. The board's line-item
 *   tax column and the order total's "مالیات و عوارض" breakdown are
 *   fabricated: `proc_purchase_order_line` has no tax field at all, and
 *   `subtotalRial`/`totalRial` are always equal in the real schema — this
 *   form shows one real total, not an invented split. Arriving from an
 *   approved request's "ساخت سفارش خرید" link pre-fills `requestRef` from
 *   the real `?requestRef=` query param the request detail page sets.
 * state reference: ready · empty · permission_denied · network_error · validation
 */
export function OrdersPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const base = slug ? tenantBasePath(slug) : '';
  const settings = useTenantSettings();
  const { unit } = useAmountDisplay();
  const [statusFilter, setStatusFilter] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const supplierFilter = searchParams.get('supplier') ?? '';

  const initialRequestRef = searchParams.get('requestRef') ?? '';
  const [supplierPartyRef, setSupplierPartyRef] = useState('');
  const [requestRef, setRequestRef] = useState(initialRequestRef);
  const [projectRef, setProjectRef] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<PurchaseOrderLineDraft[]>([{ ...EMPTY_LINE }]);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [forceShowForm, setForceShowForm] = useState(false);
  const showCreateForm =
    Boolean(requestRef) || directPurchaseOrderAllowed(settings) || forceShowForm;

  const ordersQuery = new URLSearchParams();
  if (statusFilter) ordersQuery.set('status', statusFilter);
  if (supplierFilter) ordersQuery.set('supplier', supplierFilter);
  const ordersQueryString = ordersQuery.toString();
  const orders = useApiGet<PurchaseOrderListResponse>(
    slug
      ? `${base}/api/v1/procurement/orders${ordersQueryString ? `?${ordersQueryString}` : ''}`
      : null,
    [slug, statusFilter, supplierFilter, refreshKey],
  );
  // BUG-10 / B42: only suppliers belong in a PO's party picker with limit 200.
  const parties = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/parties?role=supplier&limit=200` : null,
    [slug],
  );
  const products = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/products?limit=200` : null,
    [slug],
  );
  const uoms = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/uoms?limit=200` : null,
    [slug],
  );
  // B74: Approved purchase requests for PO conversion
  const approvedRequests = useApiGet<PurchaseRequestListResponse>(
    slug ? `${base}/api/v1/procurement/requests?status=approved&limit=200` : null,
    [slug, refreshKey],
  );

  function handleSelectRequest(ref: string) {
    setRequestRef(ref);
    if (!ref || approvedRequests.status !== 'ready') return;
    const req = approvedRequests.data.requests.find((r) => r.ref === ref);
    if (req) {
      if (req.projectRef) setProjectRef(req.projectRef);
      if (req.lines && req.lines.length > 0) {
        setLines(
          req.lines.map((l) => ({
            productRef: l.productRef,
            description: l.description ?? '',
            quantity: l.quantity,
            uomRef: l.uomRef,
            unitPriceRial: l.estimatedUnitPriceRial ?? '',
            notes: l.notes ?? '',
          })),
        );
      }
    }
  }

  // Auto-fill from query param once requests are loaded
  useState(() => {
    if (initialRequestRef && approvedRequests.status === 'ready') {
      handleSelectRequest(initialRequestRef);
    }
  });

  const partyComboboxOptions = useMemo<ComboboxOption[]>(() => {
    if (parties.status !== 'ready') return [];
    return parties.data.items.map((p) => ({
      value: String(p.code),
      label: `${p.code} — ${p.name ?? p.code}`,
    }));
  }, [parties]);

  if (!slug) return null;

  function updateLine(index: number, patch: Partial<PurchaseOrderLineDraft>) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  }
  function removeLine(index: number) {
    setLines((prev) => prev.filter((_, i) => i !== index));
  }
  function resetForm() {
    setSupplierPartyRef('');
    setRequestRef('');
    setProjectRef('');
    setOrderDate('');
    setExpectedDate('');
    setNotes('');
    setLines([{ ...EMPTY_LINE }]);
    setFieldErrors({});
  }

  const lineTotal = (l: PurchaseOrderLineDraft) => {
    const qty = Number(l.quantity || 0);
    const price = Number(l.unitPriceRial || 0);
    return Number.isFinite(qty) && Number.isFinite(price) ? qty * price : 0;
  };
  const orderTotal = lines.reduce((sum, l) => sum + lineTotal(l), 0);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    const validLines = lines.filter((l) => l.productRef && l.quantity && l.uomRef);
    if (!supplierPartyRef.trim()) {
      setFieldErrors({ supplierPartyRef: 'انتخاب تأمین‌کننده الزامی است.' });
      setError('تأمین‌کننده، تاریخ سفارش و حداقل یک قلم معتبر لازم است.');
      return;
    }
    if (!orderDate || validLines.length === 0) {
      setError('تأمین‌کننده، تاریخ سفارش و حداقل یک قلم معتبر لازم است.');
      return;
    }
    setSubmitting(true);
    try {
      await apiPost(`${base}/api/v1/procurement/orders`, {
        supplierPartyRef: supplierPartyRef.trim(),
        requestRef: requestRef.trim() || undefined,
        projectRef: projectRef.trim() || undefined,
        orderDate,
        expectedDate: expectedDate || undefined,
        notes: notes.trim() || undefined,
        lines: validLines.map((l) => ({
          productRef: l.productRef,
          description: l.description.trim() || undefined,
          quantity: l.quantity,
          uomRef: l.uomRef,
          unitPriceRial: l.unitPriceRial.trim() || '0',
          notes: l.notes.trim() || undefined,
        })),
      });
      resetForm();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError) {
        const details = err.envelope.error.details;
        const newFieldErrors: Record<string, string> = {};
        if (details && details.length > 0) {
          for (const item of details) {
            newFieldErrors[item.path] = item.message;
          }
        }
        if (
          err.envelope.error.code === 'PARTY_ROLE_INVALID' &&
          !newFieldErrors['supplierPartyRef']
        ) {
          newFieldErrors['supplierPartyRef'] = err.envelope.error.message;
        }
        setFieldErrors(newFieldErrors);
        setError(err.envelope.error.message);
      } else {
        setError('ثبت سفارش ناموفق بود.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const productOptions = products.status === 'ready' ? products.data.items : [];
  const uomOptions = uoms.status === 'ready' ? uoms.data.items : [];
  const productSelectState =
    products.status === 'loading'
      ? { placeholder: 'در حال بارگذاری کالاها…', disabled: true }
      : productOptions.length === 0
        ? { placeholder: 'کالایی در اطلاعات پایه ثبت نشده', disabled: true }
        : { placeholder: '— انتخاب کالا / خدمت —', disabled: false };
  const uomSelectState =
    uoms.status === 'loading'
      ? { placeholder: 'در حال بارگذاری واحدها…', disabled: true }
      : uomOptions.length === 0
        ? { placeholder: 'واحدی در اطلاعات پایه ثبت نشده', disabled: true }
        : { placeholder: '— انتخاب واحد —', disabled: false };

  const columns: readonly V2TableColumn<PurchaseOrder & { id: string }>[] = [
    {
      key: 'ref',
      header: 'شماره',
      render: (row) => (
        <Link
          to={`${base}/procurement/orders/${row.ref}`}
          className="ltr"
          style={{ fontFamily: 'var(--sk-font-mono)', fontSize: 13 }}
        >
          {row.ref}
        </Link>
      ),
    },
    { key: 'supplier', header: 'تأمین‌کننده', render: (row) => row.supplierPartyRef },
    {
      key: 'total',
      header: amountColumnHeader('مبلغ کل', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.totalRial, unit)}</span>,
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => (
        <V2Badge tone={STATUS_TONE[orderStatusOf(row.statusLabel) ?? 'draft'] ?? 'neutral'}>
          {row.statusLabel}
        </V2Badge>
      ),
    },
    {
      key: 'expectedDate',
      header: 'موعد تحویل',
      render: (row) => <span className="sk-num">{displayJalaliDate(row.expectedDate)}</span>,
    },
    {
      key: 'receiptStatus',
      header: 'وضعیت رسید',
      render: (row) => {
        const receivedCount = row.lines.filter(
          (l) => Number(l.receivedQuantity) >= Number(l.quantity) && Number(l.quantity) > 0,
        ).length;
        return receivedCount === 0 ? (
          <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>رسیدی ثبت نشده</span>
        ) : (
          <span className="sk-num">
            {receivedCount} از {row.lines.length} قلم
          </span>
        );
      },
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 'var(--sk-fs-xl)', fontWeight: 700 }}>
            سفارش‌های خرید
          </h1>
          <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
            مدیریت و ثبت مستقیم سفارش‌های خرید از تأمین‌کنندگان
          </div>
        </div>
        {!showCreateForm ? (
          <V2Button
            variant="primary"
            icon="plus"
            onClick={() => {
              setForceShowForm(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            + سفارش خرید جدید
          </V2Button>
        ) : null}
      </div>

      {showCreateForm ? (
        <V2Card>
          <V2CardHeader
            title="سفارش خرید جدید"
            actions={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {requestRef ? <span className="sk-tag">از درخواست {requestRef}</span> : null}
                {forceShowForm ? (
                  <V2Button variant="ghost" size="sm" onClick={() => setForceShowForm(false)}>
                    بستن فرم
                  </V2Button>
                ) : null}
              </div>
            }
          />
          <form
            onSubmit={onCreate}
            style={{ padding: '0 20px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <V2FormGrid>
              <V2FormField label="درخواست خرید مرتبط" width="wide">
                <select
                  className="sk-select"
                  value={requestRef}
                  onChange={(e) => handleSelectRequest(e.target.value)}
                >
                  <option value="">— ثبت مستقیم (بدون درخواست) —</option>
                  {approvedRequests.status === 'ready' &&
                    approvedRequests.data.requests.map((r) => (
                      <option key={r.ref} value={r.ref}>
                        {r.ref} — {r.title || r.purpose || 'درخواست تأییدشده'}{' '}
                        {r.projectRef ? `(پروژه: ${r.projectRef})` : ''}
                      </option>
                    ))}
                </select>
              </V2FormField>
              <V2FormField label="پروژه مرتبط" width="medium">
                <input
                  className="sk-input ltr"
                  placeholder="اختیاری — مثال: PRJ-0024"
                  value={projectRef}
                  onChange={(e) => setProjectRef(e.target.value)}
                />
              </V2FormField>
              <V2FormField
                label="تأمین‌کننده"
                required
                width="wide"
                error={fieldErrors['supplierPartyRef']}
              >
                <Combobox
                  options={partyComboboxOptions}
                  value={supplierPartyRef}
                  onValueChange={(val) => {
                    setSupplierPartyRef(val);
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next['supplierPartyRef'];
                      return next;
                    });
                  }}
                  placeholder="جست‌وجوی تأمین‌کننده (کد یا نام)..."
                  disabled={parties.status === 'loading'}
                />
              </V2FormField>
              <V2FormField label="تاریخ سفارش" required width="medium">
                <JalaliDateInput value={orderDate} onChange={setOrderDate} />
              </V2FormField>
              <V2FormField label="موعد تحویل" width="medium">
                <JalaliDateInput value={expectedDate} onChange={setExpectedDate} />
              </V2FormField>
            </V2FormGrid>

            {/* Same pattern as inventory's MovementForm: the CTA sits right
              beside the section title (RTL: same start edge), not pushed
              to the row's opposite edge via space-between. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>اقلام سفارش</span>
              <V2Button type="button" variant="secondary" size="sm" icon="plus" onClick={addLine}>
                افزودن قلم
              </V2Button>
            </div>
            <div className="sk-table-scroll">
              <table className="sk-table">
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>#</th>
                    {/* `*` marks only the fields `onCreate`'s own `validLines`
                      filter (and the real `PurchaseOrderLineInput` contract)
                      actually require — productRef/quantity/uomRef.
                      unitPriceRial is real, server-optional (defaults to 0),
                      matching MovementForm.tsx's established convention. */}
                    <th style={{ minWidth: 160 }}>
                      کالا / خدمت <span style={{ color: 'var(--sk-danger)' }}>*</span>
                    </th>
                    <th style={{ width: 100 }}>
                      مقدار <span style={{ color: 'var(--sk-danger)' }}>*</span>
                    </th>
                    <th style={{ width: 100 }}>
                      واحد <span style={{ color: 'var(--sk-danger)' }}>*</span>
                    </th>
                    <th style={{ width: 140 }}>قیمت واحد (ریال)</th>
                    <th style={{ width: 150 }}>جمع خط (ریال)</th>
                    <th style={{ width: 44 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => (
                    <tr key={i}>
                      <td className="sk-num">{i + 1}</td>
                      <td>
                        <select
                          className="sk-select"
                          style={{ width: '100%' }}
                          value={line.productRef}
                          disabled={productSelectState.disabled}
                          onChange={(e) => updateLine(i, { productRef: e.target.value })}
                        >
                          <option value="">{productSelectState.placeholder}</option>
                          {productOptions.map((p) => (
                            <option key={String(p.code)} value={String(p.code)}>
                              {String(p.name ?? p.code)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          className="sk-input ltr sk-num"
                          style={{ width: '100%' }}
                          value={line.quantity}
                          onChange={(e) => updateLine(i, { quantity: e.target.value })}
                        />
                      </td>
                      <td>
                        <select
                          className="sk-select"
                          style={{ width: '100%' }}
                          value={line.uomRef}
                          disabled={uomSelectState.disabled}
                          onChange={(e) => updateLine(i, { uomRef: e.target.value })}
                        >
                          <option value="">{uomSelectState.placeholder}</option>
                          {uomOptions.map((u) => (
                            <option key={String(u.code)} value={String(u.code)}>
                              {String(u.name ?? u.code)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          className="sk-input ltr sk-num"
                          style={{ width: '100%' }}
                          value={line.unitPriceRial}
                          onChange={(e) => updateLine(i, { unitPriceRial: e.target.value })}
                        />
                      </td>
                      <td className="sk-num">{lineTotal(line).toLocaleString('fa-IR')}</td>
                      <td>
                        {lines.length > 1 ? (
                          <button
                            type="button"
                            className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
                            style={{ color: 'var(--sk-danger)' }}
                            onClick={() => removeLine(i)}
                          >
                            <Icon name="x" size="sm" />
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} style={{ fontWeight: 600 }}>
                      مبلغ کل
                    </td>
                    <td colSpan={2} className="sk-num" style={{ fontWeight: 700, fontSize: 15 }}>
                      {orderTotal.toLocaleString('fa-IR')}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <V2FormField label="یادداشت" width="full">
              <textarea
                className="sk-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </V2FormField>

            <V2FormActions>
              {error ? (
                <span style={{ fontSize: 13, color: 'var(--sk-danger)', marginInlineEnd: 'auto' }}>
                  {error}
                </span>
              ) : null}
              <V2Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'در حال ثبت…' : 'ثبت سفارش'}
              </V2Button>
            </V2FormActions>
          </form>
        </V2Card>
      ) : null}

      {orders.status === 'loading' ? <LoadingState /> : null}
      {orders.status === 'error' ? (
        <StatePanel
          variant={orders.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={orders.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
        />
      ) : null}

      {orders.status === 'ready' ? (
        <V2DataGrid
          icon="cart"
          title="سفارش‌های خرید"
          subtitle={`${orders.data.orders.length.toLocaleString('fa-IR')} سفارش در دسترس شما`}
          filters={[
            {
              id: 'status',
              label: 'وضعیت',
              value: statusFilter,
              onChange: setStatusFilter,
              options: [
                { value: '', label: 'همه' },
                { value: 'draft', label: 'پیش‌نویس' },
                { value: 'submitted', label: 'ارسال‌شده' },
                { value: 'approved', label: 'تأیید‌شده' },
                { value: 'partially_received', label: 'دریافت جزئی' },
                { value: 'received', label: 'دریافت کامل' },
                { value: 'cancelled', label: 'لغو‌شده' },
              ],
            },
          ]}
          toolbarExtra={
            supplierFilter ? (
              <span className="sk-tag" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                تأمین‌کننده: {supplierFilter}
                <Link to={`${base}/procurement/orders`} aria-label="حذف فیلتر تأمین‌کننده">
                  <Icon name="x" size="sm" />
                </Link>
              </span>
            ) : undefined
          }
          columns={columns}
          rows={orders.data.orders.map((o) => ({ ...o, id: o.ref }))}
          rowKey={(o) => o.id}
          zebra
          emptyState={<V2EmptyState icon="cart" title="سفارش خریدی ثبت نشده" />}
        />
      ) : null}
    </div>
  );
}
